import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { Field, Fields } from 'redux-form';

import AutosuggestField from 'components/fields/AutosuggestField';
import TextField from 'components/fields/TextField';
import DateRangeFields from 'components/fields/DateRangeFields';

import { requestSearchUsers } from 'redux/modules/users';
import { accountLoginFail } from 'redux/modules/account';

const styles = theme => ({
  root: {
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  labelCount: {
    marginLeft: 5,
    color: theme.palette.secondary[450],
  },
});

const ClaimsFilterParameters = ({
  className,
  change,
  selectedUsers,
  selectedPayers,
  selectedOrderStatuses,
  selectedClaimStatuses,
  selectedCheckNumbers,
  contextFiltersOptions,
  actions,
  variant,
  width,
  classes,
}) => {
  const contextOrderStatuses = contextFiltersOptions
    ? contextFiltersOptions.orderStatuses : [];
  const contextInsurances = contextFiltersOptions
    ? contextFiltersOptions.insurances : [];
  const contextClaimStatuses = contextFiltersOptions
    ? contextFiltersOptions.claimStatuses : [];
  const contextCheckNumbers = contextFiltersOptions
    ? contextFiltersOptions.checkNumbers : [];
  const remainingOrderStatuses = contextOrderStatuses
    .filter(item => selectedOrderStatuses.indexOf(item.ID) === -1);
  const remainingClaimStatuses = contextClaimStatuses
    .filter(item => selectedClaimStatuses.indexOf(item.name) === -1);
  const remainingInsurances = contextInsurances
    .filter(item => selectedPayers.indexOf(item.ID) === -1);
  const remainingCheckNumbers = contextCheckNumbers
    .filter(item => selectedCheckNumbers.indexOf(item.ID) === -1);
  return (
    <Grid
      className={classNames(classes.root, className)}
      container
      justify="flex-start"
      alignItems="center"
      spacing={8}
      wrap={width === 'xs' || width === 'sm' ? 'wrap' : 'nowrap'}
    >
      <Grid item>
        <Fields
          id="Date of Creation"
          names={[
            'Date of Service From',
            'Date of Service To',
            'Date of Creation From',
            'Date of Creation To',
          ]}
          placeholder="Date Range"
          component={DateRangeFields}
          dateOfCreationLabel="Claim Submission Date"
          fullWidth
          size="lg"
        />
      </Grid>
      <Grid
        item
        xs={7}
        md={2}
      >
        <Field
          id="User Name"
          name="User Name"
          placeholder="User"
          component={AutosuggestField}
          fullWidth
          renderSuggestionContent={suggestion =>
            `${suggestion['First Name']} ${suggestion['Last Name']}`}
          renderInputContent={suggestion => `${suggestion['First Name']} ${suggestion['Last Name']}`}
          onSuggestionSelected={(suggestion) => {
            change('User', [{
              value: suggestion.ID,
              label: `${suggestion['First Name']} ${suggestion['Last Name']}`,
            }]);
            return '';
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'nin',
                    val: ['System'],
                  },
                  {
                    name: 'Full Name',
                    operation: 'startswith',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data.filter(item => selectedUsers.indexOf(item.ID) === -1),
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'nin',
                    val: ['System'],
                  },
                ],
                limit,
                offset,
              }).then(res => res.data.filter(item => selectedUsers.indexOf(item.ID) === -1),
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
          listWidth="auto"
          onlyConfirmedSelection
          clearSearchValueAfterSelection
          keepOpenAfterSelection
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={2}
      >
        <Field
          id="Order Status Name"
          name="Order Status Name"
          placeholder="Order Status"
          component={AutosuggestField}
          fullWidth
          disabled={remainingOrderStatuses.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{suggestion.name}</span>
              <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
            </div>
          )}
          renderInputContent={suggestion => suggestion.name}
          onSuggestionSelected={(suggestion) => {
            change('Order Status', [{
              value: suggestion.ID,
              label: suggestion.name,
            }]);
            return '';
          }}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(remainingOrderStatuses
                  .filter(item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(remainingOrderStatuses);
              }
            } else {
              resolve([]);
            }
          }))}
          listWidth="auto"
          onlyConfirmedSelection
          clearSearchValueAfterSelection
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Order Status', remainingOrderStatuses.map(item => ({
              value: item.ID,
              label: item.name,
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={2}
      >
        <Field
          id="Claim Status Name"
          name="Claim Status Name"
          placeholder="Claim Status"
          component={AutosuggestField}
          fullWidth
          disabled={remainingClaimStatuses.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{suggestion.name}</span>
              <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
            </div>
          )}
          renderInputContent={suggestion => suggestion.name}
          onSuggestionSelected={(suggestion) => {
            change('Claim Status', [{
              value: suggestion.name,
              label: suggestion.name,
            }]);
            return '';
          }}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(remainingClaimStatuses
                  .filter(item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(remainingClaimStatuses);
              }
            } else {
              resolve([]);
            }
          }))}
          listWidth="auto"
          onlyConfirmedSelection
          clearSearchValueAfterSelection
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Claim Status', remainingClaimStatuses.map(item => ({
              value: item.name,
              label: item.name,
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      <Grid
        item
        xs={6}
        md={2}
      >
        <Field
          id="Payer Name"
          name="Payer Name"
          placeholder="Payer"
          component={AutosuggestField}
          fullWidth
          disabled={remainingInsurances.length === 0}
          renderSuggestionContent={suggestion => (
            <div>
              <span>{suggestion['Payer Name']}</span>
              <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
            </div>
          )}
          renderInputContent={suggestion => suggestion['Payer Name']}
          onSuggestionSelected={(suggestion) => {
            change('Payer', [{
              value: suggestion.ID,
              label: suggestion['Payer Name'],
            }]);
            return '';
          }}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(remainingInsurances
                  .filter(item => item['Payer Name']
                    && item['Payer Name'].toLowerCase().indexOf(value.toLowerCase()) !== -1));
              } else {
                resolve(remainingInsurances);
              }
            } else {
              resolve([]);
            }
          }))}
          listWidth="auto"
          onlyConfirmedSelection
          clearSearchValueAfterSelection
          keepOpenAfterSelection
          onListAdornmentClick={() => {
            change('Payer', remainingInsurances.map(item => ({
              value: item.ID,
              label: item['Payer Name'],
            })));
          }}
          listAdornmentText="Select All"
        />
      </Grid>
      {variant === 'claim-md' &&
        <Grid
          item
          xs={6}
          md={2}
        >
          {/* <Field
            id="Claim Md Code"
            name="Claim Md Code"
            component={TextField}
            type="text"
            placeholder="Claim #"
            fullWidth
            errorPlacement="bottom"
            endAdornmentClearButton
          /> */}
          <Field
            id="Check Number Name"
            name="Check Number Name"
            placeholder="Check Number"
            component={AutosuggestField}
            fullWidth
            disabled={remainingCheckNumbers.length === 0}
            renderSuggestionContent={suggestion => (
              <div>
                <span>{suggestion.name}</span>
                <span className={classes.labelCount}>{`(${suggestion.count})`}</span>
              </div>
            )}
            renderInputContent={suggestion => suggestion.name}
            onSuggestionSelected={(suggestion) => {
              change('Check Number', [{
                value: suggestion.ID,
                label: suggestion.name,
              }]);
              return '';
            }}
            fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
              if (offset === 0) {
                if (value.length > 0) {
                  resolve(remainingCheckNumbers
                    .filter(item => item.name.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                } else {
                  resolve(remainingCheckNumbers);
                }
              } else {
                resolve([]);
              }
            }))}
            listWidth="auto"
            onlyConfirmedSelection
            clearSearchValueAfterSelection
            keepOpenAfterSelection
            onListAdornmentClick={() => {
              change('Check Number', remainingCheckNumbers.map(item => ({
                value: item.ID,
                label: item.name,
              })));
            }}
            listAdornmentText="Select All"
          />
        </Grid>
      }
    </Grid>
  );
};

ClaimsFilterParameters.propTypes = {
  className: PropTypes.string,
  change: PropTypes.func.isRequired,
  selectedUsers: PropTypes.arrayOf(PropTypes.number),
  selectedPayers: PropTypes.arrayOf(PropTypes.number),
  selectedOrderStatuses: PropTypes.arrayOf(PropTypes.number),
  selectedClaimStatuses: PropTypes.arrayOf(PropTypes.string),
  selectedCheckNumbers: PropTypes.arrayOf(PropTypes.number),
  contextFiltersOptions: PropTypes.shape({
    orderStatuses: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      count: PropTypes.number,
    })).isRequired,
  }),
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    labelCount: PropTypes.string.isRequired,
  }).isRequired,
};

ClaimsFilterParameters.defaultProps = {
  className: '',
  selectedUsers: [],
  selectedPayers: [],
  selectedOrderStatuses: [],
  selectedClaimStatuses: [],
  selectedCheckNumbers: [],
  contextFiltersOptions: null,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(
  withStyles(styles)(ClaimsFilterParameters),
);



// WEBPACK FOOTER //
// ./src/components/reports/ClaimsFilter/ClaimsFilterParameters.js